// src/encounter/care-plan-tab/encounter-care-plan-tab.tsx
// The encounter's "Care plan" tab (`?x=care-plan-tab`): reads the patient's care plans (C5, with B1 activities) and the
// current steps (C4) through the existing hooks and hands CarePlanTabView the state. Loading, a failed read and "no care plan"
// stay apart. Review (accept) and Sign go through B1's activity actions; the server decides who may (403 in words).
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCarePlans, patientEnrolmentsKey } from '../services/use-care-plans';
import { fetchPatientEnrolments } from '../services/care-model-api';
import { withCurrentSteps } from '../lib/care-plan-view';
import { CarePlanTabView, type CarePlanTabViewProps } from './care-plan-view';
import { activityActionError, useActivityAction } from './use-activity-action';

export function EncounterCarePlanTab({ patientId }: { patientId: string }) {
  const plans = useCarePlans(patientId);
  const enrolments = useQuery({
    queryKey: patientEnrolmentsKey(patientId),
    enabled: (plans.data?.length ?? 0) > 0,
    queryFn: ({ signal }) => fetchPatientEnrolments(patientId, signal),
  });
  const action = useActivityAction(patientId);
  const [actionError, setActionError] = useState<{ carePlanId: string; message: string } | null>(null);
  const state: CarePlanTabViewProps['state'] = plans.isError
    ? { kind: 'error' }
    : !plans.data
      ? { kind: 'loading' }
      : plans.data.length === 0
        ? { kind: 'empty' }
        : { kind: 'plans', plans: withCurrentSteps(plans.data, enrolments.isSuccess ? enrolments.data : null) };
  return (
    <CarePlanTabView
      state={state}
      busy={action.isPending}
      actionError={actionError}
      onAction={(carePlanId, activity, kind) => {
        setActionError(null);
        action.mutate(
          { carePlanId, activityId: activity.id, action: kind, revision: activity.revision },
          { onError: (e) => setActionError({ carePlanId, message: activityActionError(e) }) },
        );
      }}
    />
  );
}
